import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Loader2, ChevronDown } from 'lucide-react';
import api from '../../lib/api';
import Button from '../Button';

import type { EntityLink } from '../../types/outreach';

// Re-exported for existing consumers; the canonical definition lives in types/outreach
export type { EntityLink } from '../../types/outreach';

interface Props {
  workspaceId: string;
  selectedLinks: EntityLink[];
  onLinksChange: (links: EntityLink[]) => void;
}

interface EntityOption {
  id: string;
  display_name: string;
  metadata?: any;
}

const ENTITY_TYPES = [
  { value: '', label: 'Select type...' },
  { value: 'bill', label: 'Bill' },
  { value: 'legislator', label: 'Legislator' },
  { value: 'candidate', label: 'Candidate' },
  { value: 'ballot_initiative', label: 'Ballot Initiative' },
  { value: 'calendar_event', label: 'Calendar Event' }
];

const RelatedEntitySelector: React.FC<Props> = ({
  workspaceId,
  selectedLinks,
  onLinksChange
}) => {
  const [entityOptions, setEntityOptions] = useState<{ [key: number]: EntityOption[] }>({});
  const [loadingOptions, setLoadingOptions] = useState<{ [key: number]: boolean }>({});
  const [searchTerms, setSearchTerms] = useState<{ [key: number]: string }>({});
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [openTypeDropdown, setOpenTypeDropdown] = useState<number | null>(null);
  const [dropdownDirection, setDropdownDirection] = useState<{ [key: number]: 'up' | 'down' }>({});
  const [typeDropdownDirection, setTypeDropdownDirection] = useState<{ [key: number]: 'up' | 'down' }>({});
  const dropdownRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const typeDropdownRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const inputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});
  const typeButtonRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({});

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdown !== null) {
        const dropdownEl = dropdownRefs.current[openDropdown];
        if (dropdownEl && !dropdownEl.contains(event.target as Node)) {
          setOpenDropdown(null);
        }
      }
      if (openTypeDropdown !== null) {
        const typeDropdownEl = typeDropdownRefs.current[openTypeDropdown];
        if (typeDropdownEl && !typeDropdownEl.contains(event.target as Node)) {
          setOpenTypeDropdown(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown, openTypeDropdown]);

  // Calculate entity dropdown direction based on available space
  useEffect(() => {
    if (openDropdown !== null && inputRefs.current[openDropdown]) {
      const rect = inputRefs.current[openDropdown]!.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 220; // max-h-48 (192px) + padding/border buffer
      
      setDropdownDirection(prev => ({
        ...prev,
        [openDropdown]: spaceBelow < dropdownHeight ? 'up' : 'down'
      }));
    }
  }, [openDropdown]);

  // Calculate type dropdown direction based on available space
  useEffect(() => {
    if (openTypeDropdown !== null && typeButtonRefs.current[openTypeDropdown]) {
      const rect = typeButtonRefs.current[openTypeDropdown]!.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 220; // 5 options * ~40px + buffer
      
      setTypeDropdownDirection(prev => ({
        ...prev,
        [openTypeDropdown]: spaceBelow < dropdownHeight ? 'up' : 'down'
      }));
    }
  }, [openTypeDropdown]);

  // Debounce search
  useEffect(() => {
    const timers: { [key: number]: NodeJS.Timeout } = {};

    Object.keys(searchTerms).forEach((indexStr) => {
      const index = parseInt(indexStr);
      const link = selectedLinks[index];
      const searchTerm = searchTerms[index];

      if (link && link.entity_type && searchTerm !== undefined && openDropdown === index) {
        timers[index] = setTimeout(() => {
          fetchEntityOptions(index, link.entity_type, searchTerm);
        }, 300);
      }
    });

    return () => {
      Object.values(timers).forEach(timer => clearTimeout(timer));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerms, selectedLinks, openDropdown]);

  const fetchEntityOptions = async (index: number, entityType: string, searchTerm: string) => {
    setLoadingOptions(prev => ({ ...prev, [index]: true }));

    try {
      let endpoint = '';
      const params: any = {};

      switch (entityType) {
        case 'bill':
          endpoint = '/outreach-unified/search/bills';
          params.workspace_id = workspaceId;
          if (searchTerm) params.q = searchTerm;
          break;
        case 'legislator':
          endpoint = '/outreach-unified/search/legislators';
          params.workspace_id = workspaceId;
          if (searchTerm) params.q = searchTerm;
          break;
        case 'candidate':
          endpoint = '/outreach-unified/search/candidates';
          params.workspace_id = workspaceId;
          if (searchTerm) params.q = searchTerm;
          break;
        case 'ballot_initiative':
          endpoint = '/outreach-unified/search/ballot-initiatives';
          params.workspace_id = workspaceId;
          if (searchTerm) params.q = searchTerm;
          break;
        case 'calendar_event':
          endpoint = '/outreach-unified/search/calendar';
          params.workspace_id = workspaceId;
          if (searchTerm) params.q = searchTerm;
          break;
      }

      const response = await api.get(endpoint, { params });
      setEntityOptions(prev => ({ ...prev, [index]: response.data.data || [] }));
    } catch (error) {
      console.error('Failed to fetch entity options:', error);
      setEntityOptions(prev => ({ ...prev, [index]: [] }));
    } finally {
      setLoadingOptions(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleTypeChange = (index: number, entityType: string) => {
    const newLinks = [...selectedLinks];
    newLinks[index] = {
      entity_type: entityType as EntityLink['entity_type'],
      entity_id: '',
      entity_display_name: '',
      entity_metadata: null
    };
    onLinksChange(newLinks);

    // Immediately load options when entity type is selected
    if (entityType) {
      setSearchTerms(prev => ({ ...prev, [index]: '' }));
      fetchEntityOptions(index, entityType, '');
    } else {
      // Clear options if type is deselected
      setEntityOptions(prev => {
        const newOptions = { ...prev };
        delete newOptions[index];
        return newOptions;
      });
    }
  };

  const handleEntityChange = (index: number, entityId: string) => {
    const options = entityOptions[index] || [];
    const selectedOption = options.find(opt => opt.id === entityId);

    if (selectedOption) {
      const newLinks = [...selectedLinks];
      newLinks[index] = {
        ...newLinks[index],
        entity_id: selectedOption.id,
        entity_display_name: selectedOption.display_name,
        entity_metadata: selectedOption.metadata
      };
      onLinksChange(newLinks);
    }
  };

  const handleSearchChange = (index: number, value: string) => {
    setSearchTerms(prev => ({ ...prev, [index]: value }));
  };

  const addLink = () => {
    onLinksChange([
      ...selectedLinks,
      {
        entity_type: '' as EntityLink['entity_type'],
        entity_id: '',
        entity_display_name: '',
        entity_metadata: null
      }
    ]);
  };

  const removeLink = (index: number) => {
    const newLinks = selectedLinks.filter((_, i) => i !== index);
    onLinksChange(newLinks);

    // Clean up state for this index
    setEntityOptions(prev => {
      const newOptions = { ...prev };
      delete newOptions[index];
      return newOptions;
    });
    setSearchTerms(prev => {
      const newTerms = { ...prev };
      delete newTerms[index];
      return newTerms;
    });
    setLoadingOptions(prev => {
      const newLoading = { ...prev };
      delete newLoading[index];
      return newLoading;
    });
  };

  return (
    <div className="space-y-3">
      {selectedLinks.map((link, index) => (
        <div key={index} className="flex gap-2 items-start">
          {/* Entity Type Dropdown */}
          <div className="flex-1">
            <div className="relative" ref={el => typeDropdownRefs.current[index] = el}>
              <button
                ref={el => typeButtonRefs.current[index] = el}
                type="button"
                onClick={() => {
                  setOpenTypeDropdown(openTypeDropdown === index ? null : index);
                  setOpenDropdown(null); // Close entity dropdown if open
                }}
                className="input w-full text-left flex items-center justify-between"
              >
                <span className={link.entity_type ? 'text-text-primary dark:text-text-dark-primary' : 'text-text-tertiary dark:text-text-dark-tertiary'}>
                  {ENTITY_TYPES.find(t => t.value === link.entity_type)?.label || 'Select type...'}
                </span>
                <ChevronDown className={`w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary transition-transform ${openTypeDropdown === index ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Type Dropdown Options */}
              {openTypeDropdown === index && (
                <div className={`absolute z-50 w-full ${
                  typeDropdownDirection[index] === 'up' ? 'bottom-full mb-1' : 'mt-1'
                } bg-white dark:bg-background-dark-secondary border border-border-light dark:border-border-dark rounded-lg shadow-lg`}>
                  {ENTITY_TYPES.filter(t => t.value !== '').map((type, typeIdx) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => {
                        handleTypeChange(index, type.value);
                        setOpenTypeDropdown(null);
                      }}
                      className={`w-full text-left px-4 py-2.5 hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary text-sm text-text-primary dark:text-text-dark-primary transition-colors ${
                        typeIdx === 0 ? 'rounded-t-lg' : ''
                      } ${
                        typeIdx === ENTITY_TYPES.filter(t => t.value !== '').length - 1 ? 'rounded-b-lg' : ''
                      } ${
                        link.entity_type === type.value ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Entity Dropdown */}
          <div className="flex-1">
            {link.entity_type ? (
              <div className="relative" ref={el => dropdownRefs.current[index] = el}>
                <input
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  value={link.entity_id ? link.entity_display_name : (searchTerms[index] || '')}
                  onChange={(e) => {
                    if (!link.entity_id) {
                      handleSearchChange(index, e.target.value);
                      setOpenDropdown(index);
                    }
                  }}
                  onFocus={() => {
                    if (!link.entity_id) {
                      setOpenDropdown(index);
                      setOpenTypeDropdown(null); // Close type dropdown if open
                      if (!entityOptions[index] || entityOptions[index].length === 0) {
                        fetchEntityOptions(index, link.entity_type, searchTerms[index] || '');
                      }
                    }
                  }}
                  onClick={() => {
                    if (!link.entity_id) {
                      setOpenDropdown(index);
                      setOpenTypeDropdown(null); // Close type dropdown if open
                    }
                  }}
                  placeholder={`Search ${ENTITY_TYPES.find(t => t.value === link.entity_type)?.label || 'entity'}...`}
                  className="input pr-10"
                  disabled={!!link.entity_id}
                  readOnly={!!link.entity_id}
                />
                
                {/* Right-side icon */}
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  {loadingOptions[index] ? (
                    <Loader2 className="w-4 h-4 animate-spin text-text-tertiary dark:text-text-dark-tertiary" />
                  ) : link.entity_id ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const newLinks = [...selectedLinks];
                        newLinks[index] = {
                          ...newLinks[index],
                          entity_id: '',
                          entity_display_name: '',
                          entity_metadata: null
                        };
                        onLinksChange(newLinks);
                        setSearchTerms(prev => ({ ...prev, [index]: '' }));
                        setOpenDropdown(index);
                        fetchEntityOptions(index, link.entity_type, '');
                      }}
                      className="pointer-events-auto text-text-tertiary dark:text-text-dark-tertiary hover:text-text-primary dark:hover:text-text-dark-primary"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  ) : (
                    <ChevronDown className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
                  )}
                </div>

                {/* Dropdown Results */}
                {!link.entity_id && openDropdown === index && entityOptions[index] && entityOptions[index].length > 0 && (
                  <div className={`absolute z-50 w-full ${
                    dropdownDirection[index] === 'up' ? 'bottom-full mb-1' : 'mt-1'
                  } bg-white dark:bg-background-dark-secondary border border-border-light dark:border-border-dark rounded-lg shadow-lg`}>
                    <div className="max-h-48 overflow-y-auto overscroll-contain">
                      {entityOptions[index].map((option, optIdx) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => {
                            handleEntityChange(index, option.id);
                            setOpenDropdown(null);
                          }}
                          className={`w-full text-left px-4 py-2.5 hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary text-sm text-text-primary dark:text-text-dark-primary transition-colors ${
                            optIdx === 0 ? 'rounded-t-lg' : ''
                          } ${
                            optIdx === entityOptions[index].length - 1 ? 'rounded-b-lg' : ''
                          }`}
                        >
                          {option.display_name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* No results message */}
                {!link.entity_id && openDropdown === index && !loadingOptions[index] && entityOptions[index] && entityOptions[index].length === 0 && (
                  <div className={`absolute z-50 w-full ${
                    dropdownDirection[index] === 'up' ? 'bottom-full mb-1' : 'mt-1'
                  } bg-white dark:bg-background-dark-secondary border border-border-light dark:border-border-dark rounded-lg shadow-lg px-4 py-3`}>
                    <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary text-center">
                      No {ENTITY_TYPES.find(t => t.value === link.entity_type)?.label.toLowerCase()}s found
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <input
                type="text"
                disabled
                placeholder="Select a type first"
                className="input opacity-50 cursor-not-allowed"
              />
            )}
          </div>

          {/* Remove Button */}
          <button
            type="button"
            onClick={() => removeLink(index)}
            className="p-2 text-text-secondary dark:text-text-dark-secondary hover:text-danger-600 dark:hover:text-danger-400 transition-colors"
            title="Remove"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      ))}

      {/* Add Another Button */}
      <Button
        type="button"
        onClick={addLink}
        variant="secondary"
        size="sm"
        className="w-full"
      >
        <Plus className="w-4 h-4 mr-2" />
        Add Another
      </Button>
    </div>
  );
};

export default RelatedEntitySelector;

